/* eslint-disable indent */
/* eslint-disable no-unused-vars */
/* eslint-disable quotes */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./style.scss";
import {
  Row,
  Col,
  Image,
  Upload,
  Button,
  message,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Checkbox,
  Card,
  Space,
} from "antd";
import TableContainer from "../../../components/TableContainer";
import API from "../../../api";
import urls from "../../../api/urls";
import moment from "moment";
import { getSingleBooking } from "../../../actionsMethods/cashBookingMethods";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import ViewBookingSummary from "../../ViewBookingSummary";
import {
  UploadOutlined,
  PrinterOutlined,
  CloudUploadOutlined,
  FileTextOutlined,
  ScanOutlined,
  BarcodeOutlined,
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  EditOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import constants from "../../../constants/constants";
import Heading from "../../../components/Heading";

const {
  DRS_DETAILS,
  DRS_UPLOAD_POD,
  DRS_UPDATE_STATUS_WEB,
  DRS_UPDATE_STATUS_V2,
  DRS_RTO_REASONS,
  DRS_UNDELIVERED_REASONS,
  DRS_RTO_PDF,
  DRS_PENDING_ORDERS,
} = urls;

const { ROLES } = constants;
const { DP_OPERATOR } = ROLES;

// Custom hook for barcode scanner detection
const useBarcodeScanner = (onScan, dependencies = []) => {
  const barcodeRef = useRef("");
  const intervalRef = useRef();
  const handleKeyDown = useCallback(
    (event) => {
      // If user is typing in an editable field, let the field handle it
      const target = event.target;
      const isEditableTarget =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if (isEditableTarget) {
        return;
      }

      // Clear existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Handle Enter key - complete barcode scan
      if (event.code === "Enter") {
        if (barcodeRef.current.trim()) {
          onScan(barcodeRef.current.trim());
          barcodeRef.current = "";
        }
        return;
      }

      // Ignore all non-printable keys and special keys
      const ignoredKeys = [
        "Shift",
        "Control",
        "Alt",
        "Meta",
        "CapsLock",
        "NumLock",
        "ScrollLock",
        "Tab",
        "Escape",
        "F1",
        "F2",
        "F3",
        "F4",
        "F5",
        "F6",
        "F7",
        "F8",
        "F9",
        "F10",
        "F11",
        "F12",
        "Insert",
        "Delete",
        "Home",
        "End",
        "PageUp",
        "PageDown",
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "Pause",
        "PrintScreen",
        "ContextMenu",
      ];

      // Check if it's a special key or non-printable character
      if (
        ignoredKeys.includes(event.key) ||
        event.key.length > 1 ||
        event.ctrlKey ||
        event.altKey ||
        event.metaKey
      ) {
        return;
      }

      // Only append alphanumeric characters to the buffered barcode
      if (event.key.length === 1 && /^[a-zA-Z0-9]$/.test(event.key)) {
        barcodeRef.current += event.key;
      }

      // Reset barcode after 40ms of inactivity (scanner typically sends data quickly)
      intervalRef.current = setTimeout(() => {
        barcodeRef.current = "";
      }, 40);
    },
    [onScan, ...dependencies]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [handleKeyDown]);
};

const CpDrsDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [tableData, setTableData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [drsDetails, setDrsDetails] = useState({});
  const [bookingType, setBookingType] = useState("");
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadingPod, setUploadingPod] = useState({});
  const [isPodModalOpen, setIsPodModalOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [podForm] = Form.useForm();
  const [podFile, setPodFile] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [rtoReasons, setRtoReasons] = useState([]);
  const [undeliveredReasons, setUndeliveredReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState(null);

  // Selection state
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isBulkUpdate, setIsBulkUpdate] = useState(false);

  // RTO printing state
  const [printingRto, setPrintingRto] = useState({});
  const [bulkPrintingRto, setBulkPrintingRto] = useState(false);

  // Submit loading state
  const [submitLoading, setSubmitLoading] = useState(false);

  // Pending status modal state
  const [isPendingModalOpen, setIsPendingModalOpen] = useState(false);
  const [pendingAwbNumbers, setPendingAwbNumbers] = useState([]);
  const [updatePodLoading, setUpdatePodLoading] = useState(false);

  // Delivered status update confirmation modal state
  const [isDeliveredConfirmModalOpen, setIsDeliveredConfirmModalOpen] =
    useState(false);
  const [deliveredUpdateRecord, setDeliveredUpdateRecord] = useState(null);

  // Get user info from localStorage
  const userinfo = JSON.parse(localStorage.getItem("userinfo"));
  const userRole = userinfo?.roles?.[0];
  const userSpId = userinfo?.spId || userinfo?.premiseId;

  // POD PDF flow state
  const [allowedCPs, setAllowedCPs] = useState([]);
  const [isPodPdfFlow, setIsPodPdfFlow] = useState(true);
  const [loadingAllowedCPs, setLoadingAllowedCPs] = useState(true);

  // Barcode scanner state
  const [currentAWB, setCurrentAWB] = useState("");
  const [scannerVisible, setScannerVisible] = useState(false);

  // Filter state
  const [columnFilters, setColumnFilters] = useState({
    awbNumber: "",
    status: "",
  });
  const [searchLoading, setSearchLoading] = useState({});
  const debounceRef = useRef({});

  const getAllowedCPsForPodPdf = async () => {
    // Simulating API call delay
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          data: [5230, 4969, 1186, 397, 4885, 4965],
        });
      }, 100);
    });
  };

  // Helper function to check if current CP is allowed for POD PDF flow
  const isAllowedForPodPdf = (userSpId, allowedCPs) => {
    return allowedCPs.includes(parseInt(userSpId));
  };

  // Handle AWB scan and auto-selection
  const handleAWBScan = useCallback(
    (awbNumber) => {
      // Clean and validate AWB number
      const cleanAWB = awbNumber
        ?.toString()
        .replace(/[^a-zA-Z0-9]/g, "")
        .trim()
        .toUpperCase();

      if (!cleanAWB) {
        message.error("Invalid AWB number.");
        return;
      }

      // Check if AWB exists in the table data
      const matchingRecord = tableData.find(
        (record) => record.cAWB_No?.toString().toUpperCase() === cleanAWB
      );

      if (!matchingRecord) {
        Modal.warning({
          title: "Shipment Not Found",
          content: `AWB ${cleanAWB} is not available in this DRS.`,
          okText: "OK",
        });
        return;
      }

      // Check if record is eligible for selection
      const isEligible =
        (matchingRecord.shipmentStatus === "out for delivery" ||
          matchingRecord.shipmentStatus === "out_for_delivery" ||
          matchingRecord.shipmentStatus === "rto_out_for_delivery") &&
        !hasExceptionRequest(matchingRecord);

      if (!isEligible) {
        let reason = `Current status: ${matchingRecord.shipmentStatus}`;
        if (hasExceptionRequest(matchingRecord)) {
          reason = "This shipment has a pending exception request";
        }
        Modal.warning({
          title: "Shipment Not Eligible",
          content: `AWB ${cleanAWB} cannot be selected. ${reason}`,
          okText: "OK",
        });
        return;
      }

      // Check if already selected
      const isAlreadySelected = selectedRecords.some(
        (r) => r.cAWB_No === matchingRecord.cAWB_No
      );

      if (isAlreadySelected) {
        message.info(`AWB ${cleanAWB} is already selected.`);
        return;
      }

      // Add to selected records
      setSelectedRecords((prev) => [...prev, matchingRecord]);
      message.success(`AWB ${cleanAWB} selected successfully`);
    },
    [tableData, selectedRecords]
  );

  // Handle manual AWB input
  const handleManualAWBScan = () => {
    if (currentAWB.trim()) {
      handleAWBScan(currentAWB);
      setCurrentAWB("");
    }
  };

  // Initialize barcode scanner
  useBarcodeScanner(handleAWBScan, [tableData, selectedRecords]);

  // Fetch allowed CPs for POD PDF flow on component mount
  // useEffect(() => {
  //   const fetchAllowedCPs = async () => {
  //     try {
  //       setLoadingAllowedCPs(true);
  //       const response = await getAllowedCPsForPodPdf();
  //       if (response.status) {
  //         setAllowedCPs(response.data);
  //         // Check if current user's spId is in the allowed list
  //         const isPdfFlow = isAllowedForPodPdf(userSpId, response.data);
  //         setIsPodPdfFlow(isPdfFlow);
  //       }
  //     } catch (error) {
  //       // Default to old flow on error
  //       setIsPodPdfFlow(false);
  //     } finally {
  //       setLoadingAllowedCPs(false);
  //     }
  //   };

  //   fetchAllowedCPs();
  // }, [userSpId]);

  // Helper function to check if RTO should be hidden
  const shouldHideRTO = () => {
    // For bulk update, check if any record is ECOMM type
    if (isBulkUpdate) {
      return (
        userRole === DP_OPERATOR &&
        selectedRecords.some((record) => record?.shipmentType === "ECOMM")
      );
    }
    return userRole === DP_OPERATOR && currentRecord?.shipmentType === "ECOMM";
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      // Select all eligible records (only those that can be updated)
      const eligibleRecords = tableData.filter(
        (record) =>
          (record.shipmentStatus === "out for delivery" ||
            record.shipmentStatus === "out_for_delivery" ||
            record.shipmentStatus === "rto_out_for_delivery") &&
          !hasExceptionRequest(record) // Exclude records with exception requests
      );
      setSelectedRecords(eligibleRecords);
    } else {
      setSelectedRecords([]);
    }
  };

  const handleSelectRecord = (record, checked) => {
    if (checked) {
      setSelectedRecords((prev) => [...prev, record]);
    } else {
      setSelectedRecords((prev) =>
        prev.filter((r) => r.cAWB_No !== record.cAWB_No)
      );
      setSelectAll(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedRecords([]);
    setSelectAll(false);
  };

  const handleBulkUpdate = () => {
    if (selectedRecords.length === 0) {
      message.warning("Please select at least one record to update");
      return;
    }

    // Check if any selected records are delivered or rto_delivered
    const finalizedRecords = selectedRecords.filter(
      (record) =>
        record.shipmentStatus === "delivered" ||
        record.shipmentStatus === "rto_delivered"
    );
    if (finalizedRecords.length > 0) {
      message.warning(
        "Bulk update is not allowed for delivered or RTO delivered shipments."
      );
      return;
    }

    // Check if any selected records have exception requests
    const recordsWithRequests = selectedRecords.filter((record) =>
      hasExceptionRequest(record)
    );
    if (recordsWithRequests.length > 0) {
      message.warning(
        "Cannot update shipments that have pending exception requests."
      );
      return;
    }

    setIsBulkUpdate(true);
    setCurrentRecord(null); // Clear individual record
    // Pre-fill the form with default values for bulk update
    podForm.setFieldsValue({
      status: "",
      receiverName: "",
      receiverPhone: "",
      remarks: "",
    });
    setSelectedStatus("");
    setSelectedReason(null);
    setIsPodModalOpen(true);
  };

  // New function to handle delivery update with new API
  const handleDeliveryUpdate = (status) => {
    if (selectedRecords.length === 0) {
      message.warning("Please select at least one record to update");
      return;
    }

    // Navigate to new delivery update page with selected shipments
    navigate("/cp-drs-delivery-update", {
      state: {
        selectedShipments: selectedRecords,
        drsNumber: id,
        status: status,
        sewaSetuDrsNo: drsDetails?.sewaSetuDrsNo,
      },
    });
  };

  // Update selectAll state when individual selections change
  useEffect(() => {
    const eligibleRecords = tableData.filter(
      (record) =>
        (record.shipmentStatus === "out for delivery" ||
          record.shipmentStatus === "out_for_delivery" ||
          record.shipmentStatus === "rto_out_for_delivery") &&
        !hasExceptionRequest(record) // Exclude records with exception requests
    );

    if (
      eligibleRecords.length > 0 &&
      selectedRecords.length === eligibleRecords.length
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedRecords, tableData]);

  const componentRef = useRef();
  const dispatch = useDispatch();
  const viewBookingSummaryResponse = useSelector(
    (state) => state.appReducer.getSingleBookingReducer,
    shallowEqual
  );

  const {
    result: singleResult,
    error,
    fetching: loading2,
  } = viewBookingSummaryResponse;

  const [visibleImages, setVisibleImages] = useState(false);
  const [imagesToShow, setImagesToShow] = useState([]);

  // Function to check if current user is DP_OPERATOR
  const isDpOperator = () => {
    const userInfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
    return userInfo.roles && userInfo.roles.includes(DP_OPERATOR);
  };

  // Function to check if DRS is in draft status
  const isDraftStatus = () => {
    return drsDetails?.drsStatus?.toLowerCase() === "draft";
  };

  // Function to handle Edit DRS
  const handleEditDRS = () => {
    navigate(`/edit-drs/${id}`);
  };

  // Function to handle Finalise DRS
  const handleFinaliseDRS = () => {
    Modal.confirm({
      title: "Finalise DRS",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to finalise this DRS?</p>
          <p>
            <strong>DRS Number:</strong> {drsDetails?.sewaSetuDrsNo || id}
          </p>
          <p>
            <strong>Total Documents:</strong> {tableData.length}
          </p>
          <p style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "8px" }}>
            ⚠️ Once finalised, this DRS cannot be edited anymore.
          </p>
        </div>
      ),
      okText: "Yes, Finalise",
      cancelText: "Cancel",
      okType: "primary",
      okButtonProps: {
        style: {
          backgroundColor: "#52c41a",
          borderColor: "#52c41a",
        },
      },
      onOk: async () => {
        try {
          setLoading(true);
          const payload = {
            drsNumber: drsDetails?.DRS_number,
            drsStatus: "finalised",
            daId: drsDetails?.innoId?.toString(),
            source: "NBA_WEB",
          };

          const response = await API.Api(urls.DRS_FINALISE_API).patch(payload);

          if (response.status) {
            message.success(`DRS ${id} finalised successfully`);
            getDrsDetails();
          } else {
            throw new Error(
              response?.response?.data?.message || "Failed to finalise DRS"
            );
          }
        } catch (error) {
          message.error(error?.message || "Failed to finalise DRS");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  useEffect(() => {
    setIsPodPdfFlow(!isDpOperator());
  }, [isDpOperator]);

  const handleEyeButton = (b) => {
    setBookingType(b?.bookingType);
    setIsSummaryModalOpen(true);
    dispatch(getSingleBooking(b?.cAWB_No));
  };

  // Utility function to detect file type by URL extension
  const getFileType = (url) => {
    const extension = url.split(".").pop()?.toLowerCase();
    if (["pdf"].includes(extension)) {
      return "pdf";
    } else if (
      ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension)
    ) {
      return "image";
    }
    return "unknown";
  };

  const handlePODImages = (files) => {
    // Separate images and PDFs
    const images = files.filter((file) => getFileType(file) === "image");
    const pdfs = files.filter((file) => getFileType(file) === "pdf");

    // If there are PDFs, open them in new tabs
    if (pdfs.length > 0) {
      pdfs.forEach((pdf) => {
        window.open(pdf, "_blank");
      });
    }

    // If there are images, show them in the preview
    if (images.length > 0) {
      setImagesToShow(images);
      setVisibleImages(true);
    }

    // If no images but only PDFs, show a message
    if (images.length === 0 && pdfs.length > 0) {
      message.success(`${pdfs.length} PDF file(s) opened in new tab(s)`);
    }
  };

  const handlePodUpload = async (files, record) => {
    try {
      setUploadingPod((prev) => ({ ...prev, [record.cAWB_No]: true }));

      // Array to store all uploaded image URLs
      const uploadedUrls = [];

      // Upload each file one by one
      for (const file of files) {
        // Create form data according to the cURL format
        const formData = new FormData();
        const fileName = `${record.cAWB_No}.${file.name
          .split(".")
          .pop()
          ?.toLowerCase()}`;

        // Append file with the custom filename as the third parameter
        formData.append("files", file, fileName);
        formData.append("path", "/drs_pod");
        formData.append("fileType", file.type);
        formData.append("filename", fileName);

        // Call API to upload POD
        const response = await API.Api(DRS_UPLOAD_POD).post(formData);

        if (response.status) {
          // Extract URL from response and add to array
          const uploadedImageUrl = response?.response?.data?.data?.[0]?.url;
          if (uploadedImageUrl) {
            uploadedUrls.push(uploadedImageUrl);
          }
        } else {
          // Extract error message from response object
          const errorMsg =
            response?.response?.data?.message || "Failed to upload POD image";
          message.error(errorMsg);
        }
      }

      if (uploadedUrls.length > 0) {
        message.success(
          `Successfully uploaded ${uploadedUrls.length} ${
            isPodPdfFlow ? "PDF(s)" : "image(s)"
          }`
        );
        return uploadedUrls;
      } else {
        message.error(
          `Failed to upload any ${isPodPdfFlow ? "PDF" : "images"}`
        );
        return null;
      }
    } catch (error) {
      message.error(
        error?.message ||
          `Error uploading POD ${isPodPdfFlow ? "PDF" : "images"}`
      );
      return null;
    } finally {
      setUploadingPod((prev) => ({ ...prev, [record.cAWB_No]: false }));
    }
  };

  const updateDeliveryStatus = async (records, imageUrls, formValues) => {
    try {
      const receiverName = formValues.receiverName;
      const receiverPhone = isBulkUpdate
        ? formValues.receiverPhone
        : records?.payload?.deliveryDetails?.maskedPhoneNo ===
          formValues.receiverPhone
        ? records?.payload?.deliveryDetails?.phoneNo
        : formValues.receiverPhone;
      const deliveryRemarks = formValues.remarks || "";
      const status = formValues.status;
      const reason = formValues.reason;
      const reasonText = formValues.reasonText || "";

      let geolocation = { latitude: 0, longitude: 0 };

      try {
        if (navigator.geolocation) {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0,
            });
          });

          geolocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
        }
      } catch (locationError) {
        console.warn("Error getting location:", locationError);
        message.error(locationError?.message);
      }

      // Handle both single record and bulk update
      const cawbNumbers = Array.isArray(records)
        ? records.map((record) => record.cAWB_No)
        : [records.cAWB_No];

      // Prepare shipment groups for the new API structure
      const shipmentGroups = [
        {
          awb_numbers: cawbNumbers.map(String),
          // Add POD URL if images are provided for delivered status
          ...(status === "delivered" &&
            imageUrls &&
            imageUrls.length > 0 && {
              pod_url: imageUrls[0], // Use first image as POD URL
            }),
        },
      ];

      // Prepare API payload using new structure similar to CpDrsDeliveryUpdate
      const payload = {
        da_id: drsDetails?.innoId || "",
        drs_number: id,
        source: "NBA_WEB",
        event_timestamp: new Date().toISOString(),
        location: geolocation,
        status: status,
        ref_id: userinfo?.spId || "",
        details: {
          ...(status === "delivered" && {
            receiver_name: receiverName || "",
            receiver_mobile: receiverPhone || "",
            remarks: deliveryRemarks || "",
          }),
          ...(status !== "delivered" && {
            reason_code: reason?.value || "",
            remarks: reasonText || "",
          }),
          shipment_groups: shipmentGroups,
        },
      };

      const response = await API.Api(DRS_UPDATE_STATUS_V2).postIdHeader(
        payload
      );

      if (
        response.status &&
        (response.response?.status === 200 || response.response?.status === 201)
      ) {
        const recordCount = cawbNumbers.length;
        message.success(
          `Status updated successfully for ${recordCount} record${
            recordCount > 1 ? "s" : ""
          }`
        );
        return true;
      } else {
        throw new Error(
          response.response?.data?.message || "Failed to update status"
        );
      }
    } catch (error) {
      message.error(error?.message || "Failed to update status");
      return false;
    }
  };

  const handlePodFormSubmit = async (values) => {
    try {
      setSubmitLoading(true);
      const recordsToUpdate = isBulkUpdate ? selectedRecords : currentRecord;

      // Prevent submitting with same delivered status for already delivered shipments
      if (
        (values.status === "delivered" &&
          currentRecord?.shipmentStatus === "delivered") ||
        (values.status === "rto_delivered" &&
          currentRecord?.shipmentStatus === "rto_delivered")
      ) {
        message.error(
          "Cannot update an already delivered shipment to the same delivered status. Please select a different status."
        );
        return;
      }

      // For delivered status with PDF flow, use the new integrated API call
      if (values.status === "delivered" && isPodPdfFlow) {
        // Get geolocation
        let geolocation = { latitude: 0, longitude: 0 };
        try {
          if (navigator.geolocation) {
            const position = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0,
              });
            });

            geolocation = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };
          }
        } catch (locationError) {
          console.warn("Error getting location:", locationError);
          // Continue without location - not critical for functionality
        }

        // Prepare AWB numbers
        const awbNumbers = Array.isArray(recordsToUpdate)
          ? recordsToUpdate
              .map((record) => String(record.cAWB_No))
              .filter(Boolean)
          : [String(recordsToUpdate.cAWB_No)].filter(Boolean);

        // Prepare shipment groups without pod_url
        const shipmentGroups = [{ awb_numbers: awbNumbers }];

        // Prepare API payload similar to CpDrsDeliveryUpdate but without pod_url
        const payload = {
          da_id: drsDetails?.innoId || "",
          drs_number: id,
          source: "NBA_WEB",
          event_timestamp: new Date().toISOString(),
          location: geolocation,
          status: "delivered",
          ref_id: userinfo?.spId || "",
          details: {
            receiver_name: values.receiverName || "",
            receiver_mobile: values.receiverPhone || "",
            remarks: values.remarks || "",
            shipment_groups: shipmentGroups,
          },
        };

        // Make API call using DRS_UPDATE_STATUS_V2
        const response = await API.Api(DRS_UPDATE_STATUS_V2).postIdHeader(
          payload
        );

        if (
          response.status &&
          (response.response?.status === 200 ||
            response.response?.status === 201)
        ) {
          message.success("Status updated successfully!");

          // Clean up and refresh
          setIsPodModalOpen(false);
          setPodFile(null);
          podForm.resetFields();
          setSelectedStatus("");
          setSelectedReason(null);
          setIsBulkUpdate(false);
          getDrsDetails();
          handleClearSelection();
        } else {
          throw new Error(
            response.response?.data?.message || "Failed to update status"
          );
        }
        return;
      }

      // For other statuses (RTO, Undelivered), process normally
      let imageUrls = [];

      if (values.status === "delivered" && !isPodPdfFlow) {
        if (!podFile || podFile.length === 0) {
          message.error("POD images are required for delivered status");
          return;
        }

        // For bulk update, use the first selected record for file naming
        const recordForUpload = isBulkUpdate
          ? selectedRecords[0]
          : currentRecord;

        // Upload the images
        imageUrls = (await handlePodUpload(podFile, recordForUpload)) || [];

        if (imageUrls.length === 0) {
          message.error("Failed to upload POD images");
          return;
        }
      }

      // Then update delivery status with form values
      const success = await updateDeliveryStatus(
        recordsToUpdate,
        imageUrls,
        values
      );

      if (success) {
        setIsPodModalOpen(false);
        setPodFile(null);
        podForm.resetFields();
        setSelectedStatus("");
        setSelectedReason(null);
        setIsBulkUpdate(false);
        // Refresh the data to show the new status
        getDrsDetails();
        // Clear selections after update
        handleClearSelection();
      }
    } catch (error) {
      message.error(error?.message || "Failed to process your request");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Function to check if shipment has exception request
  const hasExceptionRequest = (record) => {
    return (
      record?.exceptionRequestStatus === "requested" && record?.reqRaisedFor
    );
  };

  const openPodModal = (record) => {
    // Check if this is a delivered or rto_delivered shipment
    if (
      record?.shipmentStatus === "delivered" ||
      record?.shipmentStatus === "rto_delivered"
    ) {
      message.warning(
        `Status update is not allowed for ${
          record?.shipmentStatus === "delivered" ? "delivered" : "RTO delivered"
        } shipments.`
      );
      return;
    }

    setIsBulkUpdate(false);
    setCurrentRecord(record);
    // Pre-fill the form with existing data if available
    podForm.setFieldsValue({
      status: record?.shipmentStatus || "",
      receiverName: record?.payload?.deliveryDetails?.name || "",
      receiverPhone: record?.payload?.deliveryDetails?.maskedPhoneNo || "",
      remarks: "",
    });
    setSelectedStatus(record?.shipmentStatus || "");
    setSelectedReason(null);
    setIsPodModalOpen(true);
  };

  // Function to proceed with delivered shipment update
  const proceedWithDeliveredUpdate = () => {
    const record = deliveredUpdateRecord;
    setIsDeliveredConfirmModalOpen(false);

    setIsBulkUpdate(false);
    setCurrentRecord(record);
    // Pre-fill the form with undelivered status for delivered shipments
    podForm.setFieldsValue({
      status: "undelivered", // Force undelivered for delivered shipments
      receiverName: record?.payload?.deliveryDetails?.name || "",
      receiverPhone: record?.payload?.deliveryDetails?.maskedPhoneNo || "",
      remarks: "",
    });
    setSelectedStatus("undelivered");
    setSelectedReason(null);
    setIsPodModalOpen(true);
  };

  // Function to handle view request button click
  const handleViewRequest = (record) => {
    Modal.info({
      title: "Exception Request Details",
      width: 500,
      content: (
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <strong>AWB Number:</strong> {record.cAWB_No}
          </div>
          <div style={{ marginBottom: 12 }}>
            <strong>Request Status:</strong>
            <Tag
              color={
                record.exceptionRequestStatus === "approved"
                  ? "green"
                  : record.exceptionRequestStatus === "rejected"
                  ? "red"
                  : "orange"
              }
              style={{ marginLeft: 8 }}
            >
              {record.exceptionRequestStatus?.toUpperCase()}
            </Tag>
          </div>
          <div style={{ marginBottom: 12 }}>
            <strong>Request Raised For:</strong> {record.reqRaisedFor}
          </div>
          <div style={{ marginBottom: 12 }}>
            <strong>Reason:</strong> {record.reason}
          </div>
          <div style={{ marginBottom: 12 }}>
            <strong>Remarks:</strong> {record.remarks}
          </div>
        </div>
      ),
      onOk() {},
    });
  };

  // Function to handle Update POD button click
  const handleUpdatePOD = async () => {
    try {
      setUpdatePodLoading(true);

      // Make API call to check pending orders
      const response = await API.Api(`${DRS_PENDING_ORDERS}?drs=${id}`).get();
      if (response.status && response.response?.data?.data) {
        const { awb_numbers = [], delivery_flag = false } =
          response.response.data.data;

        // Check response conditions
        if (awb_numbers.length === 0 && delivery_flag === false) {
          // Show error message
          message.info("POD is not required.");
          return;
        }

        if (awb_numbers.length > 0) {
          // Show pending modal with AWB numbers
          setPendingAwbNumbers(awb_numbers);
          setIsPendingModalOpen(true);
          return;
        }

        if (awb_numbers.length === 0 && delivery_flag === true) {
          // Navigate directly to delivery update page with non-ecomm shipments only
          const nonEcommShipments = tableData.filter(
            (record) => record?.shipmentType !== "ECOMM"
          );

          navigate("/cp-drs-delivery-update", {
            state: {
              selectedShipments: nonEcommShipments,
              drsNumber: id,
              status: "delivered",
              daId: drsDetails?.innoId,
              sewaSetuDrsNo: drsDetails?.sewaSetuDrsNo,
            },
          });
          return;
        }

        // Default case - should not happen but just in case
        message.error("Unable to determine POD upload requirements");
      } else {
        throw new Error(
          response.response?.data?.message || "Failed to check pending status"
        );
      }
    } catch (error) {
      message.error(error?.message || "Failed to check pending status");
    } finally {
      setUpdatePodLoading(false);
    }
  };

  // RTO Slip printing functions
  const handlePrintRtoSlip = async (record) => {
    try {
      setPrintingRto((prev) => ({ ...prev, [record.cAWB_No]: true }));

      const payload = {
        documents: [record.cAWB_No],
      };

      const response = await API.Api(DRS_RTO_PDF).post(payload);

      if (response.status && response.response?.data?.data?.pdfUrlLink) {
        // Extract PDF URL from response
        const pdfUrl = response.response.data.data.pdfUrlLink;

        // Open PDF URL in new window for printing
        const printWindow = window.open(pdfUrl, "_blank");
        if (printWindow) {
          message.success("RTO slip opened for printing");
        } else {
          // Fallback if popup is blocked
          message.error(
            "Please allow popups to view the RTO slip for printing"
          );
          return;
        }
      } else {
        // Handle different error scenarios
        const errorMessage =
          response?.response?.data?.message ||
          response?.response?.data?.data?.message ||
          "Failed to generate RTO slip - no PDF URL received";
        message.error(errorMessage);
      }
    } catch (error) {
      message.error(error?.message);
    } finally {
      setPrintingRto((prev) => ({ ...prev, [record.cAWB_No]: false }));
    }
  };

  const handleBulkPrintRtoSlip = async () => {
    try {
      setBulkPrintingRto(true);

      const rtoRecords = tableData.filter(
        (record) =>
          record.shipmentStatus === "rto_initiated" ||
          record.shipmentStatus === "RTO_INITIATED" ||
          record.shipmentStatus === "rto_undelivered" ||
          record.shipmentStatus === "RTO_UNDELIVERED" ||
          record.shipmentStatus === "rto"
      );

      if (rtoRecords.length === 0) {
        message.warning("No RTO initiated records selected");
        return;
      }

      const payload = {
        documents: rtoRecords.map((record) => record.cAWB_No),
      };

      const response = await API.Api(DRS_RTO_PDF).post(payload);

      if (response.status && response.response?.data?.data?.pdfUrlLink) {
        // Extract PDF URL from response
        const pdfUrl = response.response.data.data.pdfUrlLink;
        const responseData = response.response.data.data;

        // Open PDF URL in new window for printing
        const printWindow = window.open(pdfUrl, "_blank");
        if (printWindow) {
          // Show detailed success message with processing info
          let successMessage = `Bulk RTO slip opened for printing (${responseData.totalProcessedDocuments}/${responseData.totalRequestedDocuments} documents processed)`;

          // Add warning if some documents were filtered out
          if (
            responseData.filteredOutDocuments &&
            responseData.filteredOutDocuments.length > 0
          ) {
            successMessage += `. Note: ${responseData.filteredOutDocuments.length} document(s) were filtered out.`;
          }

          message.success(successMessage);
        } else {
          // Fallback if popup is blocked
          message.error(
            "Please allow popups to view the RTO slips for printing"
          );
          return;
        }
      } else {
        // Handle different error scenarios
        const errorMessage =
          response?.response?.data?.message ||
          response?.response?.data?.data?.message ||
          "Failed to generate RTO slips - no PDF URL received";
        message.error(errorMessage);
      }
    } catch (error) {
      message.error("Error generating bulk RTO slips");
    } finally {
      setBulkPrintingRto(false);
    }
  };

  // Filter handling functions
  const handleColumnFilter = (value, field) => {
    const updatedFilters = {
      ...columnFilters,
      [field]: value,
    };

    setColumnFilters(updatedFilters);

    if (field === "awbNumber" && value === "") {
      const queryParams = new URLSearchParams(location.search);
      if (queryParams.has("awbNumber")) {
        queryParams.delete("awbNumber");
        const newSearch = queryParams.toString();
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, {
          replace: true,
        });
      }
    }

    if (debounceRef.current[field]) {
      clearTimeout(debounceRef.current[field]);
    }

    const minChars = field === "awbNumber" ? 3 : 2;

    if (value === "" || value.length >= minChars) {
      setSearchLoading((prev) => ({
        ...prev,
        [field]: true,
      }));

      debounceRef.current[field] = setTimeout(() => {
        setSearchLoading((prev) => ({
          ...prev,
          [field]: false,
        }));

        getDrsDetails(updatedFilters);
      }, 500);
    } else {
      setSearchLoading((prev) => ({
        ...prev,
        [field]: false,
      }));
    }
  };

  const handleClearFilters = () => {
    Object.values(debounceRef.current).forEach((timeout) => {
      if (timeout) clearTimeout(timeout);
    });
    debounceRef.current = {};

    setSearchLoading({});

    setColumnFilters({
      awbNumber: "",
      status: "",
    });

    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has("awbNumber")) {
      queryParams.delete("awbNumber");
      const newSearch = queryParams.toString();
      navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`, {
        replace: true,
      });
    }

    getDrsDetails({
      awbNumber: "",
      status: "",
    });
  };

  // Helper function to create column filter for document number
  const getColumnSearchProps = (dataIndex, placeholder) => {
    const minChars = dataIndex === "awbNumber" ? 3 : 2;
    const placeholderText = `Search ${placeholder}...`;

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={placeholderText}
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleColumnFilter(value, dataIndex);
            }}
            onPressEnter={() => {
              confirm();
              if (debounceRef.current[dataIndex]) {
                clearTimeout(debounceRef.current[dataIndex]);
                debounceRef.current[dataIndex] = null;
              }
              setSearchLoading((prev) => ({
                ...prev,
                [dataIndex]: false,
              }));
              getDrsDetails();
            }}
            style={{
              marginBottom: 8,
              display: "block",
              borderRadius: "6px",
            }}
            allowClear={false}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                if (debounceRef.current[dataIndex]) {
                  clearTimeout(debounceRef.current[dataIndex]);
                  debounceRef.current[dataIndex] = null;
                }
                setSearchLoading((prev) => ({
                  ...prev,
                  [dataIndex]: false,
                }));
                if (
                  selectedKeys[0] === "" ||
                  selectedKeys[0]?.length >= minChars
                ) {
                  getDrsDetails();
                }
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
                confirm();
                if (debounceRef.current[dataIndex]) {
                  clearTimeout(debounceRef.current[dataIndex]);
                  debounceRef.current[dataIndex] = null;
                }
                setSearchLoading((prev) => ({
                  ...prev,
                  [dataIndex]: false,
                }));
                handleColumnFilter("", dataIndex);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <div className={`filter-icon-container ${filtered ? "filtered" : ""}`}>
          <FilterOutlined
            className={`filter-icon ${filtered ? "filtered" : ""}`}
          />
        </div>
      ),
      filteredValue: columnFilters[dataIndex]
        ? [columnFilters[dataIndex]]
        : null,
    };
  };

  // Helper function to create status dropdown filter
  const getStatusFilterProps = (dataIndex) => {
    const statusOptions = [
      { label: "Out for Delivery", value: "out_for_delivery" },
      { label: "Undelivered", value: "undelivered" },
      { label: "Delivered", value: "delivered" },
      { label: "RTO", value: "rto" },
      { label: "RTO - Out for Delivery", value: "rto_out_for_delivery" },
      { label: "RTO - Undelivered", value: "rto_undelivered" },
      { label: "RTO - Delivered", value: "rto_delivered" },
      { label: "Lost", value: "lost" },
      { label: "Damaged", value: "damaged" },
    ];

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Select status"
            value={selectedKeys[0]}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
              handleColumnFilter(value || "", dataIndex);
            }}
            style={{
              width: 200,
              marginBottom: 8,
              display: "block",
            }}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={statusOptions}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                if (debounceRef.current[dataIndex]) {
                  clearTimeout(debounceRef.current[dataIndex]);
                  debounceRef.current[dataIndex] = null;
                }
                setSearchLoading((prev) => ({
                  ...prev,
                  [dataIndex]: false,
                }));
                getDrsDetails();
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
                confirm();
                if (debounceRef.current[dataIndex]) {
                  clearTimeout(debounceRef.current[dataIndex]);
                  debounceRef.current[dataIndex] = null;
                }
                setSearchLoading((prev) => ({
                  ...prev,
                  [dataIndex]: false,
                }));
                handleColumnFilter("", dataIndex);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <div className={`filter-icon-container ${filtered ? "filtered" : ""}`}>
          <FilterOutlined
            className={`filter-icon ${filtered ? "filtered" : ""}`}
          />
        </div>
      ),
      filteredValue: columnFilters[dataIndex]
        ? [columnFilters[dataIndex]]
        : null,
    };
  };

  // Define the checkbox column separately
  const checkboxColumn = {
    title: (
      <Checkbox
        checked={selectAll}
        onChange={(e) => handleSelectAll(e.target.checked)}
        disabled={
          tableData.filter(
            (record) =>
              (record.shipmentStatus === "out for delivery" ||
                record.shipmentStatus === "out_for_delivery" ||
                record.shipmentStatus === "rto_out_for_delivery") &&
              !hasExceptionRequest(record) // Exclude records with exception requests
          ).length === 0
        }
      />
    ),
    dataIndex: "select",
    key: "select",
    width: 30,
    render: (text, record) => {
      const isEligible =
        (record.shipmentStatus === "out for delivery" ||
          record.shipmentStatus === "out_for_delivery" ||
          record.shipmentStatus === "rto_out_for_delivery") &&
        !hasExceptionRequest(record); // Exclude records with exception requests
      const isSelected = selectedRecords.some(
        (r) => r.cAWB_No === record.cAWB_No
      );

      return (
        <Checkbox
          checked={isSelected}
          onChange={(e) => handleSelectRecord(record, e.target.checked)}
          disabled={!isEligible}
        />
      );
    },
  };

  const baseColumns = [
    {
      title: "S.No",
      dataIndex: "sNo",
      key: "sNo",
      render: (text, record, index) =>
        record?.scannedIndex !== undefined
          ? record?.scannedIndex + 1
          : index + 1,
    },
    {
      title: "Document Number",
      dataIndex: "cAWB_No",
      key: "cAWB_No",
      ...getColumnSearchProps("awbNumber", "Document Number"),
      render: (text, record) => (
        <a
          onClick={() => handleEyeButton(record)}
          style={{ fontWeight: "500" }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Status",
      dataIndex: "shipmentStatus",
      key: "status",
      ...getStatusFilterProps("status"),
      render: (text) => {
        let color = "";
        let status = "";
        const lowerText = text?.toLowerCase() || "";

        if (lowerText === "out for delivery") {
          color = "#2E77D0";
          status = "Out for Delivery";
        } else if (lowerText === "out_for_delivery") {
          color = "#2E77D0";
          status = "Out for Delivery";
        } else if (lowerText === "delivered") {
          color = "#149D11";
          status = "Delivered";
        } else if (lowerText === "undelivered" || lowerText === "attempted") {
          color = "#FF0000";
          status = "Undelivered";
        } else if (lowerText === "rto") {
          color = "#FFA500";
          status = "RTO";
        } else if (lowerText === "rto_out_for_delivery") {
          color = "#2E77D0";
          status = "RTO - Out for Delivery";
        } else if (lowerText === "rto_delivered") {
          color = "#149D11";
          status = "RTO - Delivered";
        } else if (lowerText === "rto_undelivered") {
          color = "#FF0000";
          status = "RTO - Undelivered";
        } else {
          color = "#808080";
          status = text?.replace(/_/g, " ") || "N/A";
        }
        return (
          <Tag color={color} key={text}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Track Current Status",
      dataIndex: "trackshipmentstatus",
      key: "trackshipmentstatus",
      width: 140,
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Button
            type="default"
            size="small"
            style={{ marginTop: 2 }}
            onClick={() =>
              navigate("/shipment-tracker", {
                state: { awb: record.cAWB_No, num: Math.random() },
              })
            }
          >
            Track Shipment
          </Button>
        </div>
      ),
    },
    {
      title: "Shipping Type",
      dataIndex: "shippingType",
      key: "shippingType",
      render: (text) => text || "N/A",
      width: 100,
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (text) => moment(text).format("DD/MM/YYYY hh:mm A") || "N/A",
      width: 100,
    },
    {
      title: "Address",
      dataIndex: ["payload", "deliveryDetails", "address"],
      key: "address",
      render: (text) => <span>{text || "N/A"}</span>,
      width: 180,
    },
    {
      title: "Customer Name",
      dataIndex: ["payload", "deliveryDetails", "name"],
      key: "customerName",
      render: (text) => text || "N/A",
    },
    {
      title: "Mobile Number",
      dataIndex: ["payload", "deliveryDetails", "maskedPhoneNo"],
      key: "mobileNumber",
      render: (text) => text || "N/A",
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (text) => text || "N/A",
      width: 100,
    },
    {
      title: "POD",
      dataIndex: ["payload", "PODImages"],
      key: "PODImages",
      width: 100,
      render: (text, record) => {
        const files = record?.payload?.PODImages;
        if (files && files.length) {
          return <a onClick={() => handlePODImages(files)}>View POD</a>;
        } else {
          return <span>N/A</span>;
        }
      },
    },
    {
      title: "Eway Bill Nos",
      dataIndex: "ewayBillNos",
      key: "ewayBillNos",
      width: 160,
      render: (ewayBillNos) => {
        if (!ewayBillNos || ewayBillNos.length === 0) {
          return <span>N/A</span>;
        }
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {ewayBillNos.map((no) => (
              <Tag key={no} color="blue" style={{ margin: 0 }}>
                {no}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Request Status",
      dataIndex: "exceptionRequestStatus",
      key: "exceptionRequestStatus",
      render: (text) => {
        return (
          <Tag
            color={
              text === "approved"
                ? "green"
                : text === "rejected"
                ? "red"
                : "orange"
            }
            style={{ marginLeft: 8 }}
          >
            {text?.toUpperCase()}
          </Tag>
        );
      },
      width: 100,
    },
  ];

  const actionColumn = [
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (text, record) => {
        const isSelected = selectedRecords.some(
          (r) => r.cAWB_No === record.cAWB_No
        );
        const isEligible =
          record.shipmentStatus === "out for delivery" ||
          record.shipmentStatus === "out_for_delivery" ||
          record.shipmentStatus === "rto_out_for_delivery";

        const isRtoInitiated =
          record.shipmentStatus === "rto_initiated" ||
          record.shipmentStatus === "RTO_INITIATED" ||
          record.shipmentStatus === "rto";

        // Check if shipment has exception request
        const hasRequest = hasExceptionRequest(record);

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {!isSelected && (
              <>
                {hasRequest ? (
                  <Button
                    type="default"
                    icon={<EyeOutlined />}
                    size="small"
                    onClick={() => handleViewRequest(record)}
                  >
                    View Request
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    icon={<UploadOutlined />}
                    loading={uploadingPod[record.cAWB_No]}
                    size="small"
                    disabled={
                      isDraftStatus() ||
                      !isEligible ||
                      record.shipmentType === "ECOMM"
                    }
                    onClick={() => openPodModal(record)}
                  >
                    Update Status
                  </Button>
                )}
                {/* {isRtoInitiated && (
                  <Button
                    type="primary"
                    icon={<PrinterOutlined />}
                    loading={printingRto[record.cAWB_No]}
                    size="small"
                    onClick={() => handlePrintRtoSlip(record)}
                  >
                    Print RTO Slip
                  </Button>
                )} */}
              </>
            )}
          </div>
        );
      },
    },
  ];

  // Conditionally combine columns based on user role
  const columns = isDpOperator()
    ? [...baseColumns]
    : [checkboxColumn, ...baseColumns, ...actionColumn]; //[checkboxColumn, ...baseColumns, ...actionColumn]

  const getDrsDetails = async (filters = null) => {
    try {
      setLoading(true);

      // Build query params with filters
      const queryParams = new URLSearchParams();
      const filtersToUse = filters || columnFilters;

      if (filtersToUse.status && filtersToUse.status.trim() !== "") {
        queryParams.set("status", filtersToUse.status.trim());
      }

      if (filtersToUse.awbNumber && filtersToUse.awbNumber.trim() !== "") {
        queryParams.set("awbNumber", filtersToUse.awbNumber.trim());
      }

      const queryString = queryParams.toString();
      const apiUrl = queryString
        ? `${DRS_DETAILS}/${id}?${queryString}`
        : `${DRS_DETAILS}/${id}`;

      const { response: { data: { data } = {} } = {} } =
        (await API.Api(apiUrl).get()) || {};

      if (data) {
        const { drsDetails, drsOrdersList = [] } = data;
        setDrsDetails(drsDetails);
        setTableData(drsOrdersList);
        setTotal(drsOrdersList.length);
        // Clear selections and scanner data when data refreshes
        handleClearSelection();
      }
    } catch (error) {
      message.error(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchReasons = async () => {
    try {
      const [rtoResponse, undeliveredResponse] = await Promise.all([
        API.Api(DRS_RTO_REASONS).get(),
        API.Api(DRS_UNDELIVERED_REASONS).get(),
      ]);

      if (rtoResponse?.response?.data?.data) {
        const transformedRtoReasons = rtoResponse.response.data.data.map(
          (reason) => ({
            value: reason.reason_code,
            label: reason.description,
            _id: reason._id,
          })
        );
        setRtoReasons(transformedRtoReasons);
      }

      if (undeliveredResponse?.response?.data?.data) {
        const transformedUndeliveredReasons =
          undeliveredResponse.response.data.data.map((reason) => ({
            value: reason.reason_code,
            label: reason.description,
            _id: reason._id,
          }));
        setUndeliveredReasons(transformedUndeliveredReasons);
      }
    } catch (error) {
      message.error(error?.message);
    }
  };

  useEffect(() => {
    fetchReasons();
  }, []);

  useEffect(() => {
    if (id) {
      const queryParams = new URLSearchParams(location.search);
      const awbFilter = queryParams.get("awbNumber");

      if (awbFilter) {
        const initialFilters = {
          ...columnFilters,
          awbNumber: awbFilter,
        };
        setColumnFilters(initialFilters);
        getDrsDetails(initialFilters);
      } else {
        getDrsDetails();
      }
    }
  }, [id, location.search]);

  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceRef.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
      debounceRef.current = {};
    };
  }, []);

  function handleCancel() {
    setIsModalOpen(false);
    setIsSummaryModalOpen(false);
  }

  return (
    <div className="drs-details">
      <ViewBookingSummary
        visible={isSummaryModalOpen}
        loading={loading2}
        // handleCancelBooking={handleCancelBooking}
        handleCancel={handleCancel}
        allAppData={singleResult}
        bookingType={bookingType}
        componentRef={componentRef}
      />
      <Heading title="DRS Details" navigateTo="/drs" />
      <Row>
        <Col span={24}>
          <Row className="booking-details">
            <Col md={8} lg={8} xs={12} sm={12}>
              <div className="col">
                <p>
                  SewaSetu DRS Number: &nbsp;&nbsp;&nbsp;{" "}
                  <span className="highlight">
                    {drsDetails?.sewaSetuDrsNo || "N/A"}
                  </span>
                </p>
              </div>
            </Col>
            <Col md={8} lg={8} xs={12} sm={12}>
              <div className="col">
                <p>
                  DRS Number: &nbsp;&nbsp;&nbsp;{" "}
                  <span className="highlight">
                    {drsDetails?.DRS_number || "N/A"}
                  </span>
                </p>
              </div>
            </Col>
            <Col md={8} lg={8} xs={12} sm={12}>
              <div className="col">
                <p>
                  DA Name: &nbsp;&nbsp;&nbsp;{"  "}
                  <span className="highlight">
                    {drsDetails?.createdBy || "N/A"}
                  </span>
                </p>
              </div>
            </Col>
            <Col md={8} lg={8} xs={12} sm={12}>
              <div className="col">
                <p>
                  Phone No.: &nbsp;&nbsp;&nbsp;{"  "}
                  <span className="highlight">
                    {drsDetails?.DAMobileNumber || "N/A"}
                  </span>
                </p>
              </div>
            </Col>
            <Col md={8} lg={8} xs={12} sm={12}>
              <div className="col">
                <p>
                  DRS Status: &nbsp;&nbsp;&nbsp;{"  "}
                  <span className="highlight">
                    {drsDetails?.status || "N/A"}
                  </span>
                </p>
              </div>
            </Col>
            <Col md={8} lg={8} xs={12} sm={12}>
              <div className="col">
                <p>
                  Editable Period: &nbsp;&nbsp;&nbsp;{"  "}
                  <span className="highlight">
                    {drsDetails?.editableUntil
                      ? moment(drsDetails.editableUntil).format(
                          "DD-MM-YYYY HH:mm:ss"
                        )
                      : "N/A"}
                  </span>
                </p>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          {/* AWB Barcode Scanner Section */}
          {isPodPdfFlow && ( //!isDpOperator()
            <Card className="scanner-section">
              <Row
                justify="space-between"
                align="middle"
                className={`scanner-header ${
                  scannerVisible ? "has-content" : ""
                }`}
              >
                <Col>
                  <div className="scanner-title">
                    <BarcodeOutlined />
                    <span>AWB Barcode Scanner</span>
                  </div>
                </Col>
                <Col>
                  <Button
                    type={scannerVisible ? "default" : "primary"}
                    icon={<ScanOutlined />}
                    onClick={() => setScannerVisible(!scannerVisible)}
                    size="small"
                  >
                    {scannerVisible ? "Hide" : "Show"}
                  </Button>
                </Col>
              </Row>

              {scannerVisible && (
                <div>
                  <Row gutter={[12, 8]} align="middle">
                    <Col span={16}>
                      <Input
                        placeholder="Scan AWB number..."
                        value={currentAWB}
                        onChange={(e) => {
                          const cleanedValue = e.target.value.replace(
                            /[^a-zA-Z0-9]/g,
                            ""
                          );
                          setCurrentAWB(cleanedValue);
                        }}
                        onPressEnter={handleManualAWBScan}
                        prefix={<BarcodeOutlined />}
                        suffix={
                          currentAWB && (
                            <Button
                              type="link"
                              size="small"
                              onClick={handleManualAWBScan}
                            >
                              Scan
                            </Button>
                          )
                        }
                      />
                    </Col>
                    <Col span={8}>
                      <div className="selection-summary">
                        Selected: {selectedRecords.length}
                      </div>
                    </Col>
                  </Row>
                  <div className="scanner-tip">
                    Use barcode scanner to scan AWB labels or manually type AWB
                    numbers. Eligible shipments will be automatically selected.
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Clear Filters Button */}
          <Row
            justify="end"
            align="middle"
            className="mb-1 mr-1"
            style={{ marginTop: "16px" }}
          >
            <Col>
              <Button
                type="default"
                icon={<ClearOutlined />}
                onClick={handleClearFilters}
                disabled={
                  Object.values(columnFilters).filter(
                    (value) => value && value.trim() !== ""
                  ).length === 0
                }
              >
                Clear Filters{" "}
                {Object.values(columnFilters).filter(
                  (value) => value && value.trim() !== ""
                ).length > 0 &&
                  `(${
                    Object.values(columnFilters).filter(
                      (value) => value && value.trim() !== ""
                    ).length
                  })`}
              </Button>
            </Col>
          </Row>

          {/* {selectedRecords.length ||
            (tableData.some(
              (record) =>
                record.shipmentStatus === "rto_initiated" ||
                record.shipmentStatus === "RTO_INITIATED" ||
                record.shipmentStatus === "rto"
            ) && ( */}
          <div className="action-buttons-container">
            <div className="buttons-group">
              {!isDpOperator() && selectedRecords.length > 0 && (
                <>
                  <span className="selection-badge">
                    {selectedRecords.length} record(s) selected
                  </span>
                  <Button
                    type="default"
                    onClick={handleClearSelection}
                    className="enhanced-default-btn"
                  >
                    Clear Selection
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleBulkUpdate}
                    className="enhanced-success-btn"
                  >
                    Bulk Update Status ({selectedRecords.length})
                  </Button>
                  {/* Delivered with POD Upload redirection */}
                  {/* <Button
                    type="primary"
                    icon={<CloudUploadOutlined />}
                    onClick={() => handleDeliveryUpdate("delivered")}
                    className="enhanced-success-btn"
                    style={{
                      backgroundColor: "#52c41a",
                      borderColor: "#52c41a",
                    }}
                  >
                    Upload POD ({selectedRecords.length})
                  </Button> */}

                  {/* RTO and Undelivered using original modal */}
                  {/* <Button
                    type="default"
                    onClick={() => {
                      setIsBulkUpdate(true);
                      setCurrentRecord(null);
                      setSelectedStatus("undelivered");
                      setSelectedReason(null);
                      setIsPodModalOpen(true);
                    }}
                    className="enhanced-warning-btn"
                    style={{
                      backgroundColor: "#faad14",
                      borderColor: "#faad14",
                      color: "white",
                    }}
                  >
                    Mark as Undelivered ({selectedRecords.length})
                  </Button>
                  <Button
                    type="danger"
                    onClick={() => {
                      setIsBulkUpdate(true);
                      setCurrentRecord(null);
                      setSelectedStatus("rto");
                      setSelectedReason(null);
                      setIsPodModalOpen(true);
                    }}
                    className="enhanced-danger-btn"
                  >
                    Mark as RTO ({selectedRecords.length})
                  </Button> */}
                </>
              )}
              {/* <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                onClick={() => navigate(`/cp-drs-pod-upload/${id}`)}
                className="enhanced-upload-btn"
              >
                Upload POD
              </Button> */}
              {!isDpOperator() && (
                <Button
                  type="primary"
                  icon={<CloudUploadOutlined />}
                  onClick={handleUpdatePOD}
                  loading={updatePodLoading}
                  className="enhanced-upload-btn"
                >
                  Update POD
                </Button>
              )}
              {tableData.some(
                (record) =>
                  record.shipmentStatus === "rto_initiated" ||
                  record.shipmentStatus === "RTO_INITIATED" ||
                  record.shipmentStatus === "rto_undelivered" ||
                  record.shipmentStatus === "RTO_UNDELIVERED" ||
                  record.shipmentStatus === "rto"
              ) && (
                <Button
                  type="primary"
                  icon={<PrinterOutlined />}
                  onClick={handleBulkPrintRtoSlip}
                  loading={bulkPrintingRto}
                  className="enhanced-rto-btn"
                >
                  Bulk Print RTO Slip (
                  {
                    tableData.filter(
                      (record) =>
                        record.shipmentStatus === "rto_initiated" ||
                        record.shipmentStatus === "RTO_INITIATED" ||
                        record.shipmentStatus === "rto_undelivered" ||
                        record.shipmentStatus === "RTO_UNDELIVERED" ||
                        record.shipmentStatus === "rto"
                    ).length
                  }
                  )
                </Button>
              )}
            </div>
          </div>
          {/* ))} */}
          <TableContainer
            loading={loading}
            columns={columns}
            dataSource={tableData}
            pagination={{
              pageSize: 12,
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Col>
      </Row>
      <div style={{ display: "none" }}>
        <Image.PreviewGroup
          preview={{
            visible: visibleImages,
            onVisibleChange: (vis) => setVisibleImages(vis),
          }}
        >
          {imagesToShow.map((image, index) => (
            <Image
              key={index}
              src={image}
              alt={`POD Image ${index + 1}`}
              width={200}
              onClick={() => setVisibleImages(true)}
            />
          ))}
        </Image.PreviewGroup>
      </div>
      {/* POD Upload Modal */}
      <Modal
        title={
          <div style={{ fontSize: "18px", fontWeight: "bold" }}>
            {isBulkUpdate
              ? `Bulk Update Shipment Status (${selectedRecords.length} records)`
              : "Update Shipment Status"}
          </div>
        }
        open={isPodModalOpen}
        onCancel={() => {
          setIsPodModalOpen(false);
          setPodFile(null);
          podForm.resetFields();
          setSelectedStatus("");
          setSelectedReason(null);
          setIsBulkUpdate(false);
        }}
        footer={null}
        width={550}
        bodyStyle={{ padding: "24px" }}
        maskClosable={false}
      >
        <Form
          form={podForm}
          layout="vertical"
          onFinish={handlePodFormSubmit}
          requiredMark="optional"
          initialValues={{ status: "delivered" }}
        >
          <Form.Item
            name="status"
            label={<span style={{ fontWeight: "bold" }}>Delivery Status</span>}
            rules={[{ required: true, message: "Please select a status" }]}
          >
            <Select
              placeholder="Select status"
              size="large"
              onChange={(value) => {
                setSelectedStatus(value);
                setSelectedReason(null);
              }}
            >
              {/* For delivered shipments, only allow undelivered status */}
              {currentRecord?.shipmentStatus === "delivered" ? (
                <Select.Option value="undelivered">Undelivered</Select.Option>
              ) : (
                <>
                  <Select.Option value="delivered">Delivered</Select.Option>
                  <Select.Option value="undelivered">Undelivered</Select.Option>
                  {!shouldHideRTO() && (
                    <Select.Option value="rto">RTO</Select.Option>
                  )}
                </>
              )}
            </Select>
          </Form.Item>

          {selectedStatus === "delivered" && isPodPdfFlow ? (
            <>
              {!isBulkUpdate && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="receiverName"
                      label={
                        <span style={{ fontWeight: "bold" }}>
                          Receiver Name
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "Please enter receiver name",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter receiver name"
                        size="large"
                        disabled={
                          currentRecord?.shipmentStatus === "delivered" ||
                          currentRecord?.shipmentStatus === "rto_delivered"
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="receiverPhone"
                      initialValue={
                        currentRecord?.payload?.deliveryDetails?.maskedPhoneNo
                      }
                      label={
                        <span style={{ fontWeight: "bold" }}>
                          Receiver Phone
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "Please enter receiver phone number",
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (
                              value ===
                              currentRecord?.payload?.deliveryDetails
                                ?.maskedPhoneNo
                            ) {
                              return Promise.resolve();
                            }
                            if (/^[0-9]{10}$/.test(value)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(
                                "Please enter a valid 10-digit phone number"
                              )
                            );
                          },
                        }),
                      ]}
                    >
                      <Input
                        placeholder="Enter 10-digit phone number"
                        size="large"
                        disabled={
                          currentRecord?.shipmentStatus === "delivered" ||
                          currentRecord?.shipmentStatus === "rto_delivered"
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {isBulkUpdate && (
                <>
                  <Form.Item
                    name="receiverName"
                    label={
                      <span style={{ fontWeight: "bold" }}>Receiver Name</span>
                    }
                    rules={[
                      { required: true, message: "Please enter receiver name" },
                    ]}
                  >
                    <Input
                      placeholder="Enter receiver name for all deliveries"
                      size="large"
                      disabled={
                        currentRecord?.shipmentStatus === "delivered" ||
                        currentRecord?.shipmentStatus === "rto_delivered"
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    name="receiverPhone"
                    label={
                      <span style={{ fontWeight: "bold" }}>Receiver Phone</span>
                    }
                    rules={[
                      {
                        required: true,
                        message: "Please enter receiver phone number",
                      },
                      {
                        pattern: /^[0-9]{10}$/,
                        message: "Please enter a valid 10-digit phone number",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter 10-digit phone number for all deliveries"
                      size="large"
                      disabled={
                        currentRecord?.shipmentStatus === "delivered" ||
                        currentRecord?.shipmentStatus === "rto_delivered"
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    name="remarks"
                    label={
                      <span style={{ fontWeight: "bold" }}>
                        {isBulkUpdate
                          ? "Delivery Remarks (for all)"
                          : "Delivery Remarks"}
                      </span>
                    }
                  >
                    <Input.TextArea
                      placeholder={
                        isBulkUpdate
                          ? "Enter delivery remarks for all records"
                          : "Enter delivery remarks"
                      }
                      autoSize={{ minRows: 3, maxRows: 5 }}
                      style={{ fontSize: "14px" }}
                    />
                  </Form.Item>
                </>
              )}

              {/* Only show POD upload for non-delivered statuses */}
              {selectedStatus !== "delivered" && (
                <Form.Item
                  label={
                    <span style={{ fontWeight: "bold" }}>
                      Upload POD Image (Required - Max 3 images)
                      {isBulkUpdate && (
                        <span style={{ color: "#666", fontWeight: "normal" }}>
                          {" "}
                          - Same images will be used for all selected records
                        </span>
                      )}
                    </span>
                  }
                  required
                  rules={[
                    {
                      required: selectedStatus === "delivered",
                      message: "POD images are required for delivered status",
                    },
                  ]}
                >
                  <div
                    style={{
                      border: "1px dashed #d9d9d9",
                      borderRadius: "8px",
                      padding: "20px",
                      background: "#fafafa",
                      textAlign: "center",
                    }}
                  >
                    <Upload
                      accept="image/*"
                      multiple
                      maxCount={3}
                      beforeUpload={(file, fileList) => {
                        // Check if trying to upload more than 3 files
                        if (fileList.length > 3) {
                          message.error(
                            "You can only upload a maximum of 3 images!"
                          );
                          return Upload.LIST_IGNORE;
                        }

                        // Check file type
                        const isImage = file.type.startsWith("image/");
                        if (!isImage) {
                          message.error("You can only upload image files!");
                          return Upload.LIST_IGNORE;
                        }

                        // Check file size (limit to 5MB)
                        const isLt5M = file.size / 1024 / 1024 < 5;
                        if (!isLt5M) {
                          message.error("Image must be smaller than 5MB!");
                          return Upload.LIST_IGNORE;
                        }

                        return false; // Prevent auto upload
                      }}
                      onChange={({ fileList }) => {
                        // Update the podFile state with the current fileList
                        setPodFile(
                          fileList.map((file) => file.originFileObj || file)
                        );
                      }}
                      fileList={podFile || []}
                      onRemove={(file) => {
                        const newFileList =
                          podFile?.filter((f) => f.uid !== file.uid) || [];
                        setPodFile(newFileList.length > 0 ? newFileList : null);
                      }}
                      listType="picture"
                    >
                      <Button
                        icon={<UploadOutlined />}
                        disabled={podFile && podFile.length >= 3}
                        type="primary"
                        ghost
                        size="large"
                        style={{ marginBottom: "8px" }}
                      >
                        Select Image
                        {podFile && podFile.length > 0
                          ? ` (${podFile.length}/3)`
                          : ""}
                      </Button>
                      <div style={{ color: "#888", fontSize: "13px" }}>
                        Click to upload (max 3 images)
                      </div>
                    </Upload>
                  </div>
                </Form.Item>
              )}

              {/* Message for delivered status */}
              {/* {selectedStatus === "delivered" && (
                <div
                  style={{
                    padding: "16px",
                    backgroundColor: "#e6f7ff",
                    border: "1px solid #91d5ff",
                    borderRadius: "8px",
                    marginBottom: "16px",
                  }}
                >
                  <p style={{ margin: 0, color: "#1890ff", fontWeight: "500" }}>
                    📤 For delivered status, POD pdf will be uploaded on the
                    next page after clicking &quot;Submit&quot;.
                  </p>
                </div>
              )} */}
            </>
          ) : (
            <>
              {selectedStatus === "delivered" && (
                <>
                  {!isBulkUpdate && (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="receiverName"
                          label={
                            <span style={{ fontWeight: "bold" }}>
                              Receiver Name
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Please enter receiver name",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Enter receiver name"
                            size="large"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="receiverPhone"
                          initialValue={
                            currentRecord?.payload?.deliveryDetails
                              ?.maskedPhoneNo
                          }
                          label={
                            <span style={{ fontWeight: "bold" }}>
                              Receiver Phone
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Please enter receiver phone number",
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (
                                  value ===
                                  currentRecord?.payload?.deliveryDetails
                                    ?.maskedPhoneNo
                                ) {
                                  return Promise.resolve();
                                }
                                if (/^[0-9]{10}$/.test(value)) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(
                                  new Error(
                                    "Please enter a valid 10-digit phone number"
                                  )
                                );
                              },
                            }),
                          ]}
                        >
                          <Input
                            placeholder="Enter 10-digit phone number"
                            size="large"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  {isBulkUpdate && (
                    <>
                      <Form.Item
                        name="receiverName"
                        label={
                          <span style={{ fontWeight: "bold" }}>
                            Receiver Name
                          </span>
                        }
                        rules={[
                          {
                            required: true,
                            message: "Please enter receiver name",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Enter receiver name for all deliveries"
                          size="large"
                        />
                      </Form.Item>
                      <Form.Item
                        name="receiverPhone"
                        label={
                          <span style={{ fontWeight: "bold" }}>
                            Receiver Phone
                          </span>
                        }
                        rules={[
                          {
                            required: true,
                            message: "Please enter receiver phone number",
                          },
                          {
                            pattern: /^[0-9]{10}$/,
                            message:
                              "Please enter a valid 10-digit phone number",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Enter 10-digit phone number for all deliveries"
                          size="large"
                        />
                      </Form.Item>
                    </>
                  )}

                  <Form.Item
                    label={
                      <span style={{ fontWeight: "bold" }}>
                        Upload POD Image (Required - Max 3 images)
                        {isBulkUpdate && (
                          <span style={{ color: "#666", fontWeight: "normal" }}>
                            {" "}
                            - Same images will be used for all selected records
                          </span>
                        )}
                      </span>
                    }
                    required
                    rules={[
                      {
                        required: true,
                        message: "POD images are required for delivered status",
                      },
                    ]}
                  >
                    <div
                      style={{
                        border: "1px dashed #d9d9d9",
                        borderRadius: "8px",
                        padding: "20px",
                        background: "#fafafa",
                        textAlign: "center",
                      }}
                    >
                      <Upload
                        accept="image/*"
                        multiple
                        maxCount={3}
                        beforeUpload={(file, fileList) => {
                          // Check if trying to upload more than 3 files
                          if (fileList.length > 3) {
                            message.error(
                              "You can only upload a maximum of 3 images!"
                            );
                            return Upload.LIST_IGNORE;
                          }

                          // Check file type
                          const isImage = file.type.startsWith("image/");
                          if (!isImage) {
                            message.error("You can only upload image files!");
                            return Upload.LIST_IGNORE;
                          }

                          // Check file size (limit to 5MB)
                          const isLt5M = file.size / 1024 / 1024 < 5;
                          if (!isLt5M) {
                            message.error("Image must be smaller than 5MB!");
                            return Upload.LIST_IGNORE;
                          }

                          return false; // Prevent auto upload
                        }}
                        onChange={({ fileList }) => {
                          // Update the podFile state with the current fileList
                          setPodFile(
                            fileList.map((file) => file.originFileObj || file)
                          );
                        }}
                        fileList={podFile || []}
                        onRemove={(file) => {
                          const newFileList =
                            podFile?.filter((f) => f.uid !== file.uid) || [];
                          setPodFile(
                            newFileList.length > 0 ? newFileList : null
                          );
                        }}
                        listType="picture"
                      >
                        <Button
                          icon={<UploadOutlined />}
                          disabled={podFile && podFile.length >= 3}
                          type="primary"
                          ghost
                          size="large"
                          style={{ marginBottom: "8px" }}
                        >
                          Select Image
                          {podFile && podFile.length > 0
                            ? ` (${podFile.length}/3)`
                            : ""}
                        </Button>
                        <div style={{ color: "#888", fontSize: "13px" }}>
                          Click to upload (max 3 images)
                        </div>
                      </Upload>
                    </div>
                  </Form.Item>

                  <Form.Item
                    name="remarks"
                    label={
                      <span style={{ fontWeight: "bold" }}>
                        {isBulkUpdate
                          ? "Delivery Remarks (for all)"
                          : "Delivery Remarks"}
                      </span>
                    }
                  >
                    <Input.TextArea
                      placeholder={
                        isBulkUpdate
                          ? "Enter delivery remarks for all records"
                          : "Enter delivery remarks"
                      }
                      autoSize={{ minRows: 3, maxRows: 5 }}
                      style={{ fontSize: "14px" }}
                    />
                  </Form.Item>
                </>
              )}
            </>
          )}

          {(selectedStatus === "undelivered" || selectedStatus === "rto") && (
            <>
              <Form.Item
                name="reason"
                label={<span style={{ fontWeight: "bold" }}>Reason</span>}
                rules={[{ required: true, message: "Please select a reason" }]}
              >
                <Select
                  placeholder="Select reason"
                  size="large"
                  onChange={(value, option) => setSelectedReason(option)}
                  labelInValue
                >
                  {selectedStatus === "undelivered" &&
                    undeliveredReasons.map((reason, index) => (
                      <Select.Option
                        key={index}
                        value={reason.value || reason.reason_code}
                        label={reason.label || reason.description}
                      >
                        {reason.label || reason.description}
                      </Select.Option>
                    ))}
                  {selectedStatus === "rto" &&
                    rtoReasons.map((reason, index) => (
                      <Select.Option
                        key={index}
                        value={reason.value || reason.reason_code}
                        label={reason.label || reason.description}
                      >
                        {reason.label || reason.description}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="reasonText"
                label={<span style={{ fontWeight: "bold" }}>Remarks</span>}
              >
                <Input.TextArea
                  placeholder="Enter remarks"
                  autoSize={{ minRows: 3, maxRows: 5 }}
                  style={{ fontSize: "14px" }}
                />
              </Form.Item>
            </>
          )}

          <Form.Item style={{ marginTop: "24px", textAlign: "right" }}>
            <Button
              onClick={() => {
                setIsPodModalOpen(false);
                setPodFile(null);
                podForm.resetFields();
                setSelectedStatus("");
                setSelectedReason(null);
                setIsBulkUpdate(false);
              }}
              style={{ marginRight: 12 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              disabled={
                selectedStatus === "out for delivery" ||
                selectedStatus === "out_for_delivery" ||
                selectedStatus === "rto_out_for_delivery"
              }
              loading={
                Object.values(uploadingPod).some((v) => v) || submitLoading
              }
            >
              {selectedStatus === "delivered"
                ? "Submit"
                : isBulkUpdate
                ? `Update ${selectedRecords.length} Records`
                : "Submit"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Pending Status Updates Modal */}
      <Modal
        title={
          <div
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            Pending Status Updates
          </div>
        }
        open={isPendingModalOpen}
        onCancel={() => {
          setIsPendingModalOpen(false);
          setPendingAwbNumbers([]);
        }}
        footer={[
          <Button
            key="back"
            onClick={() => {
              setIsPendingModalOpen(false);
              setPendingAwbNumbers([]);
            }}
            style={{
              width: "100%",
              height: "45px",
              fontSize: "16px",
              borderRadius: "8px",
              border: "2px solid #1890ff",
              color: "#1890ff",
              backgroundColor: "transparent",
            }}
          >
            Back
          </Button>,
        ]}
        centered
        width={450}
        bodyStyle={{
          padding: "20px",
          textAlign: "center",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <div style={{ marginBottom: "15px", textAlign: "center" }}>
            <div
              style={{
                fontSize: "14px",
                color: "#1890ff",
                fontWeight: "600",
                marginBottom: "5px",
              }}
            >
              DRS Number: {id}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#52c41a",
                fontWeight: "600",
              }}
            >
              Pending Shipments: {pendingAwbNumbers.length}
            </div>
          </div>
          <p
            style={{
              fontSize: "16px",
              color: "#666",
              marginBottom: "20px",
              lineHeight: "1.5",
            }}
          >
            Update all shipment statuses before uploading POD.
          </p>

          {pendingAwbNumbers.length > 0 && (
            <div
              style={{
                backgroundColor: "#f5f5f5",
                padding: "15px",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                maxHeight: "300px",
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  justifyContent: "center",
                }}
              >
                {pendingAwbNumbers.map((awb, index) => (
                  <Tag
                    key={index}
                    style={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "#333",
                      backgroundColor: "#fff",
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      margin: "0",
                    }}
                  >
                    {awb}
                  </Tag>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Delivered Status Update Confirmation Modal */}
      <Modal
        title={
          <div
            style={{ fontSize: "18px", fontWeight: "bold", color: "#faad14" }}
          >
            <ExclamationCircleOutlined
              style={{ marginRight: "8px", color: "#faad14" }}
            />
            Confirm Status Update
          </div>
        }
        open={isDeliveredConfirmModalOpen}
        onCancel={() => {
          setIsDeliveredConfirmModalOpen(false);
          setDeliveredUpdateRecord(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsDeliveredConfirmModalOpen(false);
              setDeliveredUpdateRecord(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="proceed"
            type="primary"
            onClick={proceedWithDeliveredUpdate}
          >
            Proceed
          </Button>,
        ]}
        width={500}
        centered
      >
        <div style={{ padding: "16px 0" }}>
          <div style={{ marginBottom: "16px" }}>
            <strong>AWB Number:</strong> {deliveredUpdateRecord?.cAWB_No}
          </div>
          <div style={{ marginBottom: "16px" }}>
            <strong>Current Status:</strong>
            <Tag color="green" style={{ marginLeft: "8px" }}>
              DELIVERED
            </Tag>
          </div>

          <div
            style={{
              backgroundColor: "#e6f7ff",
              border: "1px solid #91d5ff",
              borderRadius: "6px",
              padding: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                color: "#0050b3",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              ℹ️ Status Update Information
            </div>
            <div
              style={{ color: "#003a8c", fontSize: "14px", lineHeight: "1.5" }}
            >
              A request will be sent to your assigned hub for approval of the
              status change.
            </div>
          </div>

          <div style={{ fontSize: "14px", color: "#666" }}>
            Are you sure you want to proceed with updating this shipment?
          </div>
        </div>
      </Modal>

      {/* Hidden container for open source scanner libraries */}
      <div id="scanner-container" style={{ display: "none" }}></div>
    </div>
  );
};

export default CpDrsDetails;
